import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy loading de componentes
const LoginView = () => import('@/views/LoginView.vue')
const RegisterView = () => import('@/views/RegisterView.vue')
const FamiliesView = () => import('@/views/FamiliesView.vue')
const CreateFamilyView = () => import('@/views/CreateFamilyView.vue')
const FamilyDetailView = () => import('@/views/FamilyDetailView.vue')
const TasksView = () => import('@/views/TasksView.vue')
const TaskDetailView = () => import('@/views/TaskDetailView.vue')
// Agenda portada de mobile: Día con carril horario, Semana y Mes.
const AgendaView = () => import('@/views/AgendaView.vue')
// ADR-019 fase 5: lo mío en todas mis familias.
const MyAgendaView = () => import('@/views/MyAgendaView.vue')
const ProfileView = () => import('@/views/ProfileView.vue')
// EP-07: servicios externos conectados por la persona (no por la familia: una conexión es suya).
const ConnectionsView = () => import('@/views/ConnectionsView.vue')
// Invitaciones: la pantalla a la que lleva el enlace que comparte quien invita.
const AcceptInvitationView = () => import('@/views/AcceptInvitationView.vue')
const AcceptShareView = () => import('@/views/AcceptShareView.vue')
// Verificación del correo: la pantalla a la que lleva el enlace del correo.
const VerifyEmailView = () => import('@/views/VerifyEmailView.vue')
const ForgotPasswordView = () => import('@/views/ForgotPasswordView.vue')
const ResetPasswordView = () => import('@/views/ResetPasswordView.vue')
const ChatHomeView = () => import('@/views/ChatHomeView.vue')
const FamilyManagementDashboard = () => import('@/views/FamilyManagementDashboard.vue')
// Inicio: la pantalla de aterrizaje, con el diseño del mockup. Sustituye a
// DashboardView, que era en su mayor parte un marcador.
const HomeView = () => import('@/views/HomeView.vue')

const routes: RouteRecordRaw[] = [
  {
    // Entrar en la app aterriza en Inicio, igual que en mobile (plan §4.3).
    path: '/',
    redirect: '/inicio'
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      redirectIfAuth: true
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: {
      requiresAuth: false,
      redirectIfAuth: true
    }
  },
  {
    // Pedir el enlace: como /login, quien ya ha entrado no tiene nada que hacer aquí.
    path: '/forgot-password',
    name: 'ForgotPassword',
    alias: '/olvide-contrasena',
    component: ForgotPasswordView,
    meta: {
      requiresAuth: false,
      redirectIfAuth: true
    }
  },
  {
    path: '/chat',
    name: 'Chat',
    component: ChatHomeView,
    meta: {
      requiresAuth: true,
      // Pantalla app-like (header + lista + compositor): se dimensiona al
      // viewport exacto y el scroll vive dentro, nunca en el documento.
      appShell: true
    }
  },
  {
    path: '/inicio',
    name: 'Home',
    component: HomeView,
    meta: {
      requiresAuth: true
    }
  },
  {
    // Se conserva la ruta antigua para no romper enlaces guardados.
    path: '/dashboard',
    redirect: '/inicio'
  },
  {
    path: '/families',
    name: 'Families',
    alias: '/familia',
    component: FamiliesView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/families/create',
    name: 'CreateFamily',
    component: CreateFamilyView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/families/:id',
    name: 'FamilyDetail',
    component: FamilyDetailView,
    meta: {
      requiresAuth: true
    }
  },
  {
    // Retirada (auditoría UX §2 D): usaba `mockFamilies` y duplicaba el chat real.
    path: '/families/:id/chat',
    redirect: '/chat'
  },
  {
    path: '/tasks',
    name: 'Tasks',
    alias: '/tareas',
    component: TasksView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/tasks/:id',
    name: 'TaskDetail',
    component: TaskDetailView,
    meta: {
      requiresAuth: true
    }
  },
  {
    // La Agenda portada de mobile (Día / Semana / Mes). Sustituye a la vista
    // sobre ocurrencias, retirada con su ruta `/calendar/clasico` (EP-04 fase 7).
    path: '/calendar',
    name: 'Calendar',
    alias: '/agenda',
    component: AgendaView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/mi-agenda',
    name: 'MyAgenda',
    component: MyAgendaView,
    meta: {
      requiresAuth: true
    }
  },
  {
    // Retirada (auditoría UX §2 D): la vista Día de Agenda ya cubre lo mismo.
    path: '/calendar/daily',
    redirect: '/agenda'
  },
  {
    // Retirada (auditoría UX §2 D): "Ahora mismo" vive en Inicio.
    path: '/now',
    redirect: '/inicio'
  },
  {
    path: '/profile',
    name: 'Profile',
    alias: '/perfil',
    component: ProfileView,
    meta: {
      requiresAuth: true
    }
  },
  {
    // EP-07 · Fase 6. Cuelga de «Tu cuenta» y no de la familia: la autorización de una conexión se
    // resuelve por persona, nunca por familia (ADR-007 §2), y la pantalla tiene que decir lo mismo
    // que dice el servidor.
    path: '/connections',
    name: 'Connections',
    alias: '/conexiones',
    component: ConnectionsView,
    meta: {
      requiresAuth: true
    }
  },
  {
    // Retirada (auditoría UX §2 D): montaba la segunda implementación de chat, abandonada.
    path: '/demo/chat',
    redirect: '/chat'
  },
  {
    path: '/admin',
    name: 'FamilyManagement',
    component: FamilyManagementDashboard,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    // Aceptar una invitación a una familia. VA DECLARADA AQUÍ, ANTES DEL CATCH-ALL: una ruta que no
    // existe no da 404, la recoge `/:pathMatch(.*)*` de abajo y redirige a `/inicio`, que a su vez
    // rebota a `/login` sin conservar el destino. El enlace de invitación se perdería entero —con
    // su token— y nadie vería un solo error.
    //
    // `requiresAuth: false` y, DELIBERADAMENTE, SIN `redirectIfAuth`: aquí llega gente en los dos
    // estados. Quien ya tiene cuenta suele llegar con la sesión abierta, y `redirectIfAuth` le
    // mandaría a `/inicio` tirando el token por el camino, que es justo la mitad del requisito.
    path: '/invitations/accept',
    name: 'AcceptInvitation',
    alias: '/invitacion',
    component: AcceptInvitationView,
    meta: {
      requiresAuth: false
    }
  },
  {
    // ADR-019 4c: aceptar a una persona que comparte otra familia. Mismo criterio que /invitations/accept:
    // pública, SIN `redirectIfAuth` y antes del catch-all, o el token se pierde en silencio.
    path: '/compartir',
    name: 'AcceptShare',
    component: AcceptShareView,
    meta: {
      requiresAuth: false
    }
  },
  {
    // Mismo criterio que /invitations/accept: el enlace del correo se abre con sesión y sin ella,
    // así que es pública y SIN `redirectIfAuth` (que tiraría el token). Y va antes del catch-all.
    path: '/verify-email',
    name: 'VerifyEmail',
    alias: '/verificar-correo',
    component: VerifyEmailView,
    meta: {
      requiresAuth: false
    }
  },
  {
    // El enlace del correo: mismo criterio que /verify-email, pública y SIN `redirectIfAuth`, que
    // mandaría a Inicio a quien tenga una sesión abierta en este navegador y tiraría el token.
    path: '/reset-password',
    name: 'ResetPassword',
    alias: '/restablecer-contrasena',
    component: ResetPasswordView,
    meta: {
      requiresAuth: false
    }
  },
  {
    // Igual que los demás por defecto: se aterriza en Inicio, no en el chat.
    path: '/:pathMatch(.*)*',
    redirect: '/inicio'
  }
]

// Fábrica del router: la usa la app (una sola vez, más abajo) y también
// `router/index.spec.ts`, que necesita una instancia nueva por test — con el
// router compartido, dos redirects seguidos al MISMO destino final son una
// navegación duplicada para vue-router y el segundo test mediría el
// `redirectedFrom` del anterior.
export function createAppRouter() {
  const router = createRouter({
    history: createWebHistory(),
    routes,
  })

  // Guard de navegación para autenticación
  router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore()

    // En una carga en frío este guard corre ANTES de que se monte `App`: sin esperar aquí a que se
    // restaure la sesión, `isAuthenticated` sería falso y mandaría a `/login` a quien ya ha entrado.
    await authStore.ensureSessionRestored()

    // Verificar si la ruta requiere autenticación
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next('/login')
      return
    }

    // Verificar si la ruta requiere permisos de administrador
    if (to.meta.requiresAdmin && !authStore.user?.isAdmin) {
      next('/inicio')
      return
    }

    // Redirigir usuarios autenticados lejos de login/register
    if (to.meta.redirectIfAuth && authStore.isAuthenticated) {
      next('/inicio')
      return
    }

    next()
  })

  return router
}

const router = createAppRouter()

export default router